import {
  model,
  Model,
  prop,
  idProp,
  registerRootStore,
  modelAction,
  Frozen,
  frozen,
} from "mobx-keystone";
import { computed } from "mobx";
import { createContext } from "react";
import {
  summariseDecodedInvoice,
  DecodedInvoiceSummary,
  DecodeRequestError,
} from "../helpers/invoiceSummary";
import { trackGoal } from "../../helpers/trackGoal";

/** What people actually paste: "lightning:lnbc…" from a QR code, line breaks, spaces. */
export const normaliseInvoiceInput = (input: string): string =>
  input
    .trim()
    .replace(/^lightning:/i, "")
    .replace(/\s+/g, "")
    .toLowerCase();

@model("exrails/DecodeInvoiceStore")
export class DecodeInvoiceStore extends Model({
  id: idProp,
  invoiceInput: prop<string>(""),
  decodedInvoice: prop<Frozen<DecodedInvoiceSummary> | null>(null),
  decodeError: prop<string | null>(null),
  hasAttemptedDecode: prop<boolean>(false),
  isDecoding: prop<boolean>(false),
}) {
  @modelAction
  setInvoiceInput = (input: string) => {
    this.invoiceInput = input;
    this.hasAttemptedDecode = false;
    this.decodeError = null;
    this.decodedInvoice = null;
  };

  @modelAction
  setDecoding = (isDecoding: boolean) => {
    this.isDecoding = isDecoding;
  };

  @modelAction
  setDecodeResult = (
    result: DecodedInvoiceSummary | null,
    error: string | null
  ) => {
    if (result) {
      this.decodedInvoice = frozen(result);
      this.decodeError = null;
    } else {
      this.decodedInvoice = null;
      this.decodeError = error;
    }
    this.hasAttemptedDecode = true;
    this.isDecoding = false;
  };

  decodeInvoice = async () => {
    this.setDecoding(true);
    const trimmed = normaliseInvoiceInput(this.invoiceInput);

    if (!trimmed) {
      this.setDecodeResult(null, "Please enter a BOLT11 invoice");
      return;
    }

    try {
      const summary = await summariseDecodedInvoice(trimmed);
      this.setDecodeResult(summary, null);
      trackGoal("Invoice decoded", { result: "ok" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to decode invoice";
      this.setDecodeResult(null, message);
      const limited = error instanceof DecodeRequestError && error.status === 429;
      trackGoal("Invoice decoded", { result: limited ? "limited" : "invalid" });
    }
  };

  @modelAction
  clear = () => {
    this.invoiceInput = "";
    this.decodedInvoice = null;
    this.decodeError = null;
    this.hasAttemptedDecode = false;
  };

  @computed
  get amountSatoshis(): number | null {
    return this.decodedInvoice?.data.amountInSatoshis ?? null;
  }

  @computed
  get description(): string | null {
    return this.decodedInvoice?.data.description ?? null;
  }

  @computed
  get paymentHash(): string | null {
    return this.decodedInvoice?.data.paymentHash ?? null;
  }

  @computed
  get timestamp(): number | null {
    return this.decodedInvoice?.data.timestamp ?? null;
  }

  @computed
  get expiry(): number | null {
    return this.decodedInvoice?.data.expiry ?? null;
  }

  @computed
  get isExpired(): boolean {
    if (!this.timestamp || !this.expiry) return false;
    const expiryTime = (this.timestamp + this.expiry) * 1000;
    return Date.now() > expiryTime;
  }

  @computed
  get expiryDate(): Date | null {
    if (!this.timestamp || !this.expiry) return null;
    return new Date((this.timestamp + this.expiry) * 1000);
  }

  @computed
  get createdDate(): Date | null {
    if (!this.timestamp) return null;
    return new Date(this.timestamp * 1000);
  }

  @computed
  get network(): string | null {
    return this.decodedInvoice?.data.network ?? null;
  }

  @computed
  get routeHints(): Array<
    {
      pubkey: string;
      short_channel_id: string;
      fee_base_msat: number;
      fee_proportional_millionths: number;
      cltv_expiry_delta: number;
    }[]
  > {
    return this.decodedInvoice?.data.routeHints ?? [];
  }

  @computed
  get allPubkeys(): string[] {
    const pubkeys: string[] = [];
    if (!this.decodedInvoice) return pubkeys;

    // Extract from route hints
    for (const hint of this.routeHints) {
      for (const hop of hint) {
        if (hop.pubkey && !pubkeys.includes(hop.pubkey)) {
          pubkeys.push(hop.pubkey);
        }
      }
    }

    return pubkeys;
  }

  @computed
  get paymentSecret(): string | null {
    return this.decodedInvoice?.data.paymentSecret ?? null;
  }

  @computed
  get minFinalCltvExpiry(): number | null {
    return this.decodedInvoice?.data.minFinalCltvExpiry ?? null;
  }

  @computed
  get destination(): string | null {
    return this.decodedInvoice?.data.destination ?? null;
  }

  @computed
  get destinationAlias(): string | null {
    return this.decodedInvoice?.data.destinationAlias ?? null;
  }

  @computed
  get pubkeyAliases(): Record<string, string | null> {
    return this.decodedInvoice?.data.pubkeyAliases ?? {};
  }

  /** Get alias for a pubkey, returns null if not found */
  getAliasForPubkey(pubkey: string): string | null {
    return this.pubkeyAliases[pubkey] ?? null;
  }
}

// Create singleton store instance at module load
const runCreateDecodeInvoiceStore = () => {
  const store = new DecodeInvoiceStore({});
  registerRootStore(store);
  return store;
};

const createdStore = runCreateDecodeInvoiceStore();

// Context for React components - pre-initialized with the singleton
export const DecodeInvoiceStoreContext =
  createContext<DecodeInvoiceStore>(createdStore);
